import { differenceInDays } from "date-fns";

import { GROUP_SUNO } from "constants/staff";
import type { CurrentUser } from "features/auth/auth-slice";
import { MIGRATION_RECORDS_SOURCES } from "services/types/migration-records.type";
import { parseBackendDateOnly } from "utils/common";

export type IntercomIdentifyPayload = {
  name: string;
  user_id: string;
  email: string;
  created_at: number;
  avatar: {
    type: string;
    image_url: string;
  };
  suno_env: string;
  user_role: string;
  user_groups: string;
  internal_user: boolean;
  company:
    | {
        company_id: string;
        name: string;
        go_live_date?: number;
        new_customer?: boolean;
        previous_oms?: string;
      }
    | undefined;
};

// ENG-1526: internal Suno staff get NO company attach. Attaching the active practice as a company
// on every practice switch accumulated one company membership per switched practice — to Intercom
// that's "a new user for that company", and per-company campaigns (new-customer check-ins,
// Spotlights) fired for each. Internal users stay a single practice-less contact, marked
// `internal_user` so Intercom audiences can exclude them.
export const buildIntercomIdentifyPayload = (
  user: CurrentUser,
  sunoEnv: string
): IntercomIdentifyPayload => {
  const name = `${user.first_name.trim()} ${user.last_name.trim()}`.trim();
  const isInternalUser = Boolean(
    user.groups?.some((group) => group.name === GROUP_SUNO)
  );

  return {
    email: user.email,
    user_id: user.id.toString(),
    name,
    created_at: new Date(user.date_joined!).getTime() / 1000,
    avatar: {
      type: "avatar",
      image_url: user.photo
    },
    user_role: user.role ?? "",
    user_groups: user.groups.map((g) => g.name).join(", "),
    suno_env: sunoEnv,
    internal_user: isInternalUser,
    company:
      !isInternalUser && user.practice
        ? {
            company_id: user.practice.id.toString(),
            name: user.practice.name || "",
            previous_oms: user.practice.previous_oms
              ? MIGRATION_RECORDS_SOURCES.getFromCode(
                  user.practice.previous_oms
                )
              : "",
            go_live_date: user.practice.go_live_at
              ? parseBackendDateOnly(user.practice.go_live_at).getTime() / 1000
              : undefined,
            new_customer: user.practice.go_live_at
              ? differenceInDays(
                  new Date(),
                  parseBackendDateOnly(user.practice.go_live_at)
                ) < 14 // go live date is less than 2 weeks ago
              : false
          }
        : undefined
  };
};

/**
 * Where the Messenger launcher sits, in px from the bottom of the window. Intercom's
 * own minimum is 20; set here rather than in the Messenger settings so the side
 * menu's footer can count on it (ENG-2535).
 */
export const INTERCOM_LAUNCHER_CORNER_PADDING_PX = 20;
/** Clear of the Marker.io Help pill, which sits in the same corner where that pill shows. */
export const INTERCOM_LAUNCHER_ABOVE_HELP_PILL_PX = 56;

export default class IntercomSingleton {
  private static instance: IntercomSingleton;
  private booted: boolean;

  private constructor() {
    this.booted = false;
    this.boot();
  }

  public boot(): void {
    if (typeof window.Intercom !== "function") return;
    window.Intercom("boot", {
      api_base: "https://api-iam.intercom.io",
      app_id: "s0mg59ro",
      alignment: "left",
      vertical_padding: INTERCOM_LAUNCHER_ABOVE_HELP_PILL_PX
    });
    this.booted = true;
  }

  /** Moves the launcher up or down the left edge; Intercom repaints it in place. */
  public setLauncherVerticalPadding(px: number): void {
    if (typeof window.Intercom !== "function") return;
    window.Intercom("update", { vertical_padding: px });
  }

  public static getInstance(): IntercomSingleton {
    if (!IntercomSingleton.instance) {
      IntercomSingleton.instance = new IntercomSingleton();
    }
    return IntercomSingleton.instance;
  }

  public identify(userData: IntercomIdentifyPayload): void {
    if (typeof window.Intercom !== "function") return;
    if (!IntercomSingleton.instance.booted) {
      IntercomSingleton.instance.boot();
    }
    window.Intercom("update", userData);
  }
  public logout() {
    if (typeof window.Intercom !== "function") return;
    window.Intercom("shutdown");
    this.booted = false;
  }
}

declare global {
  interface Window {
    Intercom: {
      (command: string, params?: any): any;
    };
  }
}
